import { apiGet } from './apiClient';

export interface ArticleListItem {
  id: string;
  slug: string;
  title: string;
  summary: string;
  category: string;
  article_type: 'insight' | 'alert' | 'digest';
  topic_tags: string[];
  hero_image_url: string | null;
  author: string;
  published_at: string | null;
  read_time_minutes: number;
}

export interface ChartPoint {
  label: string;
  value: number;
}

export interface ArticleChart {
  title: string;
  unit: string;
  data: ChartPoint[];
}

export interface ArticleBody {
  what_happened?: string;
  why_it_matters?: string;
  impact_on_merchants?: string;
  affected_regions?: string;
  key_takeaways?: string[];
  charts?: ArticleChart[];
  [key: string]: unknown;
}

export interface ArticleSource {
  title: string;
  url: string;
  date?: string;
}

export interface ArticleDetail extends ArticleListItem {
  // DMG Central-sourced articles (Phase Q) carry body_html instead of the
  // structured body_json sections -- exactly one of the two is populated.
  body_json: ArticleBody | null;
  body_html: string | null;
  sources: ArticleSource[];
  affected_markets: string[];
  related: ArticleListItem[];
}

export interface TopicCount {
  category: string;
  count: number;
}

export interface ArticleListParams {
  category?: string;
  tag?: string;
  type?: string;
  limit?: number;
  offset?: number;
}

export function fetchArticles(
  params: ArticleListParams = {},
): Promise<{ articles: ArticleListItem[]; total: number; limit: number; offset: number }> {
  return apiGet('/api/articles', params as Record<string, string | number | undefined>);
}

export function fetchArticle(slug: string): Promise<{ article: ArticleDetail }> {
  return apiGet(`/api/articles/${encodeURIComponent(slug)}`);
}

export function fetchTopics(): Promise<{ topics: TopicCount[] }> {
  return apiGet('/api/articles/topics');
}

export function fetchLatest(limit = 6): Promise<{ articles: ArticleListItem[] }> {
  return apiGet('/api/articles/latest', { limit });
}

export function fetchAlerts(limit = 6): Promise<{ articles: ArticleListItem[] }> {
  return apiGet('/api/articles/alerts', { limit });
}

export interface SearchResults {
  tools: { name: string; path: string }[];
  articles: { title: string; slug: string; category: string }[];
  countries: { name: string; alpha2: string }[];
  topics: string[];
}

export function fetchSearch(q: string): Promise<SearchResults> {
  return apiGet('/api/search', { q });
}

export function formatPublishedDate(iso: string | null): string {
  if (!iso) return 'Draft';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Compact "time ago" label for homepage headlines/alerts; falls back to an
// absolute date once an item is older than a few weeks.
export function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'Draft';
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  if (Number.isNaN(then) || diffMs < 0) return formatPublishedDate(iso);

  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  return formatPublishedDate(iso);
}
